import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState, useCallback } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { BRAND_MINT, COMPLIANCE_BADGE } from '@/lib/utils'
import {
  getAuditLogApi,
  getAuditActionsApi,
  type AuditLogEntry,
  type AuditAction,
} from '@/lib/api'
import {
  LoaderIcon,
  SearchIcon,
  FilterIcon,
  XIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BuildingIcon,
  GlobeIcon,
  ActivityIcon,
  EyeIcon,
  EyeOffIcon,
  ClockIcon,
  HashIcon,
} from 'lucide-react'

/**
 * Category badge color map.
 */
const CATEGORY_STYLES: Record<string, string> = {
  auth: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  org: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  billing: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  user: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  admin: 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
  circle: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400',
  integration: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
  communication: 'bg-pink-500/10 text-pink-600 dark:text-pink-400',
  sales: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
  compliance: COMPLIANCE_BADGE,
}

function getCategoryStyle(category: string | null): string {
  return CATEGORY_STYLES[category ?? ''] ?? 'bg-muted text-muted-foreground'
}

function formatActionLabel(action: string, description: string | null): string {
  return description ?? action.replace(/_/g, ' ').replace(/\./g, ' — ')
}

/**
 * Format a timestamp for display.
 */
function formatTimestamp(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const Route = createFileRoute('/dashboard/admin/audit')({
  component: () => {
    const [entries, setEntries] = useState<AuditLogEntry[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [limit] = useState(50)
    const [loading, setLoading] = useState(true)

    // Catalog-driven filter options (GET /admin/audit-actions) so the
    // Action / Entity-type dropdowns never drift from the constants table
    const [auditActions, setAuditActions] = useState<AuditAction[]>([])
    const [entityTypes, setEntityTypes] = useState<string[]>([])

    // Filter state
    const [actionFilter, setActionFilter] = useState('')
    const [entityTypeFilter, setEntityTypeFilter] = useState('')
    const [entityIdFilter, setEntityIdFilter] = useState('')
    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')
    const [showFilters, setShowFilters] = useState(false)

    // Expanded JSON rows
    const [expandedRow, setExpandedRow] = useState<number | null>(null)

    const totalPages = Math.max(1, Math.ceil(total / limit))

    const buildParams = useCallback(() => {
      const params: Record<string, string | number> = { page, limit }
      if (actionFilter) params.action = actionFilter
      if (entityTypeFilter) params.entity_type = entityTypeFilter
      if (entityIdFilter && !isNaN(Number(entityIdFilter)) && Number(entityIdFilter) > 0) {
        params.entity_id = Number(entityIdFilter)
      }
      if (fromDate) params.from = fromDate
      if (toDate) params.to = toDate
      return params
    }, [page, limit, actionFilter, entityTypeFilter, entityIdFilter, fromDate, toDate])

    // Load catalog-driven filter options once (independent of the list query)
    useEffect(() => {
      let cancelled = false

      async function loadFilterOptions() {
        try {
          const res = await getAuditActionsApi()
          if (!cancelled) {
            setAuditActions(res.actions)
            setEntityTypes(res.entity_types)
          }
        } catch {
          // Non-fatal: dropdowns fall back to "All actions" / "All types"
        }
      }

      loadFilterOptions()
      return () => { cancelled = true }
    }, [])

    useEffect(() => {
      let cancelled = false

      async function load() {
        setLoading(true)
        try {
          const res = await getAuditLogApi(buildParams())
          if (!cancelled) {
            setEntries(res.data)
            setTotal(res.total)
          }
        } catch (err) {
          if (!cancelled) {
            toast.error(
              err instanceof Error ? err.message : 'Failed to load audit log',
            )
          }
        } finally {
          if (!cancelled) setLoading(false)
        }
      }

      load()
      return () => { cancelled = true }
    }, [buildParams])

    const handleSearch = () => {
      setPage(1)
    }

    const clearFilters = () => {
      setActionFilter('')
      setEntityTypeFilter('')
      setEntityIdFilter('')
      setFromDate('')
      setToDate('')
      setPage(1)
    }

    const hasFilters = !!(actionFilter || entityTypeFilter || entityIdFilter || fromDate || toDate)

    const toggleExpand = (logId: number) => {
      setExpandedRow(expandedRow === logId ? null : logId)
    }

    // --------------- Loading state ---------------

    if (loading && entries.length === 0) {
      return (
        <div className="mx-auto max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 shrink-0"
                style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
              />
              <h1 className="text-2xl font-semibold">Audit log</h1>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading audit log…</p>
          </div>
        </div>
      )
    }

    // --------------- Empty state ---------------

    if (entries.length === 0 && !loading) {
      return (
        <div className="mx-auto max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 shrink-0"
                style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
              />
              <div>
                <h1 className="text-2xl font-semibold">Audit log</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Track admin and super_admin actions across the platform.
                </p>
              </div>
            </div>
          </div>

          {/* Filter bar */}
          <RenderFilters
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            actionFilter={actionFilter}
            setActionFilter={setActionFilter}
            entityTypeFilter={entityTypeFilter}
            setEntityTypeFilter={setEntityTypeFilter}
            entityIdFilter={entityIdFilter}
            setEntityIdFilter={setEntityIdFilter}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
            onSearch={handleSearch}
            onClear={clearFilters}
            hasFilters={hasFilters}
            auditActions={auditActions}
            entityTypes={entityTypes}
          />

          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <SearchIcon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">No entries found</p>
            <p className="text-sm text-muted-foreground">
              {hasFilters
                ? 'No audit log entries match the current filters.'
                : 'No admin actions have been recorded yet.'}
            </p>
            {hasFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters} className="mt-2 gap-1.5">
                <XIcon className="size-4" />
                Clear filters
              </Button>
            )}
          </div>
        </div>
      )
    }

    // --------------- Main list ---------------

    return (
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 shrink-0"
              style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
            />
            <div className="flex flex-1 items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold">Audit log</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Track admin and super_admin actions across the platform.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="gap-1.5 shrink-0"
              >
                <FilterIcon className="size-4" />
                {showFilters ? 'Hide filters' : 'Filters'}
                {hasFilters && (
                  <span className="flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
                    !
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <RenderFilters
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          actionFilter={actionFilter}
          setActionFilter={setActionFilter}
          entityTypeFilter={entityTypeFilter}
          setEntityTypeFilter={setEntityTypeFilter}
          entityIdFilter={entityIdFilter}
          setEntityIdFilter={setEntityIdFilter}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          onSearch={handleSearch}
          onClear={clearFilters}
          hasFilters={hasFilters}
          auditActions={auditActions}
          entityTypes={entityTypes}
        />

        {/* Results count */}
        <p className="mb-4 text-xs text-muted-foreground">
          {total} entr{total === 1 ? 'y' : 'ies'}
          {page > 1 && ` — page ${page} of ${totalPages}`}
        </p>

        {/* Entry list */}
        <div className="space-y-2">
          {entries.map((entry) => {
            const isExpanded = expandedRow === entry.log_id
            const hasChanges = entry.old_values || entry.new_values

            return (
              <div
                key={entry.log_id}
                className="rounded-xl border border-border bg-card transition-shadow hover:shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => toggleExpand(entry.log_id)}
                  className="flex w-full items-start gap-4 p-4 text-left"
                >
                  {/* Action icon */}
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                    <ActivityIcon className="size-4 text-muted-foreground" />
                  </div>

                  {/* Main content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-foreground">
                        {entry.admin_name || `User #${entry.admin_user_id}`}
                      </span>
                      <span className="text-muted-foreground/50">·</span>
                      <span className="font-medium text-foreground">
                        {formatActionLabel(entry.action, entry.action_description)}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <BuildingIcon className="size-3" />
                        {entry.entity_type}
                        {entry.entity_id != null && ` #${entry.entity_id}`}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <ClockIcon className="size-3" />
                        {formatTimestamp(entry.created_at)}
                      </span>
                      {entry.action_category && (
                        <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${getCategoryStyle(entry.action_category)}`}>
                          {entry.action_category}
                        </span>
                      )}
                      {entry.ip_address && (
                        <span className="inline-flex items-center gap-1">
                          <GlobeIcon className="size-3" />
                          {entry.ip_address}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        {entry.admin_role.replace('_', ' ')}
                      </span>
                    </div>
                  </div>

                  {/* Expand indicator + changes badge */}
                  <div className="flex shrink-0 items-center gap-2">
                    {hasChanges && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                        <HashIcon className="size-3" />
                        changes
                      </span>
                    )}
                    {isExpanded ? (
                      <EyeOffIcon className="size-4 text-muted-foreground" />
                    ) : (
                      <EyeIcon className="size-4 text-muted-foreground" />
                    )}
                  </div>
                </button>

                {/* Expanded JSON diff */}
                {isExpanded && hasChanges && (
                  <div className="border-t border-border px-4 pb-4 pt-3">
                    <div className="grid gap-4 sm:grid-cols-2">
                      {entry.old_values && (
                        <div>
                          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                            Before
                          </p>
                          <pre className="overflow-x-auto rounded-lg bg-muted p-3 text-xs leading-relaxed">
                            {JSON.stringify(entry.old_values, null, 2)}
                          </pre>
                        </div>
                      )}
                      {entry.new_values && (
                        <div>
                          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                            After
                          </p>
                          <pre className="overflow-x-auto rounded-lg bg-muted p-3 text-xs leading-relaxed">
                            {JSON.stringify(entry.new_values, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Expanded — no changes to show */}
                {isExpanded && !hasChanges && (
                  <div className="border-t border-border px-4 pb-4 pt-3">
                    <p className="text-xs text-muted-foreground">
                      No detailed change data recorded for this action.
                    </p>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="gap-1.5"
            >
              <ChevronLeftIcon className="size-4" />
              Previous
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="gap-1.5"
            >
              Next
              <ChevronRightIcon className="size-4" />
            </Button>
          </div>
        )}
      </div>
    )
  },
})

// ── Filter sub-component ──────────────────────────────────────────────

function RenderFilters({
  showFilters,
  setShowFilters,
  actionFilter,
  setActionFilter,
  entityTypeFilter,
  setEntityTypeFilter,
  entityIdFilter,
  setEntityIdFilter,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  onSearch,
  onClear,
  hasFilters,
  auditActions,
  entityTypes,
}: {
  showFilters: boolean
  setShowFilters: (v: boolean) => void
  actionFilter: string
  setActionFilter: (v: string) => void
  entityTypeFilter: string
  setEntityTypeFilter: (v: string) => void
  entityIdFilter: string
  setEntityIdFilter: (v: string) => void
  fromDate: string
  setFromDate: (v: string) => void
  toDate: string
  setToDate: (v: string) => void
  onSearch: () => void
  onClear: () => void
  hasFilters: boolean
  auditActions: AuditAction[]
  entityTypes: string[]
}) {
  if (!showFilters) return null

  return (
    <div className="mb-6 rounded-xl border border-border bg-card p-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Action filter */}
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Action
          </label>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          >
            <option value="">All actions</option>
            {auditActions.map((a) => (
              <option key={a.action} value={a.action}>
                {formatActionLabel(a.action, a.description)}
              </option>
            ))}
          </select>
          {auditActions.length === 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              Filter options unavailable — catalog failed to load.
            </p>
          )}
        </div>

        {/* Entity type filter */}
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Entity type
          </label>
          <select
            value={entityTypeFilter}
            onChange={(e) => setEntityTypeFilter(e.target.value)}
            className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          >
            <option value="">All types</option>
            {entityTypes.map((t) => (
              <option key={t} value={t}>
                {t
                  .split('_')
                  .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
                  .join(' ')}
              </option>
            ))}
          </select>
        </div>

        {/* Entity ID filter */}
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Entity ID
          </label>
          <input
            type="number"
            min={1}
            value={entityIdFilter}
            onChange={(e) => setEntityIdFilter(e.target.value)}
            placeholder="e.g. 7"
            className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>

        {/* From date */}
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            From
          </label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>

        {/* To date */}
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            To
          </label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>

        {/* Empty cell for alignment */}
        <div className="flex items-end gap-2">
          <Button size="sm" onClick={onSearch} className="gap-1.5">
            <SearchIcon className="size-4" />
            Search
          </Button>
          {hasFilters && (
            <Button variant="outline" size="sm" onClick={onClear} className="gap-1.5">
              <XIcon className="size-4" />
              Clear
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
